import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Book, BookCreatePayload, BookStatus } from '../../books/types';
import { DEFAULT_BOOK_COLOR, formatRoman, getCurrentPacificDate, normalizeBookColor } from '../../books/utils';

interface BookEditorDialogProps {
  open: boolean;
  book?: Book | null;
  onClose: () => void;
  onSubmit: (payload: BookCreatePayload) => Promise<void> | void;
}

interface BookFormState {
  title: string;
  author: string;
  color: string;
  totalPages: string;
  introPages: string;
  currentPage: string;
  currentSection: 'intro' | 'main';
  status: BookStatus;
  startDate: string;
  targetDate: string;
}

const buildInitialState = (book?: Book | null): BookFormState => ({
  title: book?.title ?? '',
  author: book?.author ?? '',
  color: book?.color ?? DEFAULT_BOOK_COLOR,
  totalPages: String(book?.total_pages ?? ''),
  introPages: String(book?.intro_pages ?? 0),
  currentPage: String(book?.current_page ?? 0),
  currentSection: book?.current_section ?? 'main',
  status: book?.status ?? 'reading',
  startDate: book?.start_date ?? getCurrentPacificDate(),
  targetDate: book?.target_date ?? '',
});

const BookEditorDialog: React.FC<BookEditorDialogProps> = ({ open, book, onClose, onSubmit }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [submitting, setSubmitting] = React.useState(false);
  const [form, setForm] = React.useState<BookFormState>(() => buildInitialState(book));

  React.useEffect(() => {
    if (open) {
      setForm(buildInitialState(book));
      setSubmitting(false);
    }
  }, [book, open]);

  const parsedIntroPages = Math.max(Number.parseInt(form.introPages, 10) || 0, 0);
  const parsedCurrentPage = Math.max(Number.parseInt(form.currentPage, 10) || 0, 0);
  const parsedTotalPages = Math.max(Number.parseInt(form.totalPages, 10) || 0, 0);
  const currentPageHint =
    form.currentSection === 'intro' && parsedCurrentPage > 0
      ? `Displays as ${formatRoman(parsedCurrentPage)} in the tracker`
      : 'Used to pre-populate the current reading progress';

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        title: form.title.trim(),
        author: form.author.trim(),
        color: normalizeBookColor(form.color),
        total_pages: parsedTotalPages,
        intro_pages: parsedIntroPages,
        current_page: parsedCurrentPage,
        current_section: form.currentSection,
        start_date: form.startDate,
        target_date: form.targetDate,
        status: form.status,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{book ? 'Edit Book' : 'Add Book'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Title"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            required
            fullWidth
          />
          <TextField
            label="Author"
            value={form.author}
            onChange={(event) => setForm((current) => ({ ...current, author: event.target.value }))}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              label="Color"
              type="color"
              value={form.color}
              onChange={(event) => setForm((current) => ({ ...current, color: normalizeBookColor(event.target.value) }))}
              helperText="Used across the reading calendar, histogram, and progress slider"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: { xs: '100%', sm: 160 } }}
            />
            <Box
              sx={{
                height: 44,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: form.color,
                flex: 1,
              }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Numbered Pages"
              type="number"
              value={form.totalPages}
              onChange={(event) => setForm((current) => ({ ...current, totalPages: event.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Intro Pages"
              type="number"
              value={form.introPages}
              onChange={(event) => setForm((current) => ({ ...current, introPages: event.target.value }))}
              helperText="Roman numeral pages before page 1"
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Progress Section"
              value={form.currentSection}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  currentSection: event.target.value as 'intro' | 'main',
                }))
              }
              fullWidth
            >
              <MenuItem value="intro">Introduction</MenuItem>
              <MenuItem value="main">Main Pages</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  status: event.target.value as BookStatus,
                }))
              }
              fullWidth
            >
              <MenuItem value="reading">Reading</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={form.currentSection === 'intro' ? 'Intro Page' : 'Current Page'}
              type="number"
              value={form.currentPage}
              onChange={(event) => setForm((current) => ({ ...current, currentPage: event.target.value }))}
              helperText={currentPageHint}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              value={form.targetDate}
              onChange={(event) => setForm((current) => ({ ...current, targetDate: event.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.title.trim() || parsedTotalPages <= 0 || !form.targetDate || submitting}
        >
          {book ? 'Save Changes' : 'Add Book'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookEditorDialog;
